import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import useDraggableScroll from '../../hooks/useDraggableScroll';
import useScrollButtons from '../../hooks/useScrollButtons';
import { UISystemIcon } from '../icons/custom/system-icon';
import { CalendarPanel, CalendarPanelProps } from './calendar-panel';
import * as styles from './calendar-grids.scss';
import { useIntl } from 'react-intl';
export interface CalendarGridsProps {
  className?: string;
  data: CalendarPanelProps[];
  scrollToOnInit?: Date;
  style?: React.CSSProperties;
}

const SINGLE_MONTH_WIDTH = 594;

export const CalendarGrids = memo(({ className, data, scrollToOnInit, style }: CalendarGridsProps) => {
  const intl = useIntl();
  const { tabId } = useParams();

  const containerRef = useRef<HTMLDivElement>(null);

  const { onMouseDown } = useDraggableScroll(containerRef, {
    direction: 'horizontal'
  });
  const { onScrollLeft, onScrollRight } = useScrollButtons(containerRef, undefined, SINGLE_MONTH_WIDTH);

  const scrollToMonth = useCallback(
    (targetDate: Date) => {
      if (data && containerRef.current) {
        const index = data.findIndex(month => {
          const d = month.date;
          return d.getFullYear() === targetDate.getFullYear() && d.getMonth() === targetDate.getMonth();
        });
        if (index) {
          containerRef.current.scrollLeft = index * SINGLE_MONTH_WIDTH;
        }
      }
    },
    [data]
  );

  // Scroll to date grid (if specified)
  useEffect(() => {
    if (containerRef.current && scrollToOnInit) {
      scrollToMonth(scrollToOnInit);
    }
  }, [scrollToMonth, scrollToOnInit, tabId]);

  if (!data || !data.length) {
    return null;
  }

  return (
    <div className={clsx(className, styles.container)}>
      <div className={styles.ctaBar}>
        <button
          className={clsx(styles.scrollIconButton, styles.arrowPrevContainer)}
          onClick={onScrollLeft}
          data-testid="scrollLeft"
          aria-label={intl.formatMessage({
            id: 'common.scroll_left.aria_label',
            defaultMessage: 'Scroll left button'
          })}
        >
          <UISystemIcon iconName="chevron-left-thick" />
        </button>

        <button
          className={clsx(styles.scrollIconButton, styles.arrowNextContainer)}
          onClick={onScrollRight}
          data-testid="scrollRight"
          aria-label={intl.formatMessage({
            id: 'common.scroll_right.aria_label',
            defaultMessage: 'Scroll right button'
          })}
        >
          <UISystemIcon iconName="chevron-left-thick" style={{ transform: 'rotate(180deg)' }} />
        </button>
      </div>

      <div
        className={styles.scrollContainer}
        style={style}
        ref={containerRef}
        onMouseDown={onMouseDown}
        tabIndex={0}
        aria-label={intl.formatMessage({
          id: 'common.scrollable_calendar_grid.aria_label',
          defaultMessage: 'Scrollable calendar grid'
        })}
      >
        {data.map((monthData, i) => (
          <CalendarPanel
            className={i > 0 ? styles.gridGap : undefined}
            key={monthData.date.valueOf()}
            style={{
              marginLeft: i > 0 ? '20px' : undefined
            }}
            {...monthData}
          />
        ))}
      </div>
    </div>
  );
});
